import React from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { CircleX } from "lucide-react";
import { Button } from "@/components/atoms";
import "./ConfirmDialog.scss";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: "primary" | "danger";
  onConfirm: () => void | boolean;
  confirmActionTrackSufix?: string;
  cancelActionTrackSufix?: string;
  showCancel?: boolean;
}

/**
 * Accessible confirmation dialog for destructive backoffice actions.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title = "¿Eliminar este registro?",
  description = "Se eliminará de forma permanente. Esta acción no se puede deshacer.",
  confirmLabel = "Eliminar",
  cancelLabel = "Cancelar",
  confirmVariant = "danger",
  onConfirm,
  confirmActionTrackSufix = "bo_confirm",
  cancelActionTrackSufix = "bo_cancel",
  showCancel = true,
}) => {
  const handleConfirm = (event: React.MouseEvent<HTMLButtonElement>): void => {
    if (onConfirm() === false) {
      event.preventDefault();
      return;
    }

    onOpenChange(false);
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="confirm-dialog__overlay">
        <AlertDialog.Content className="confirm-dialog__content">
          <div className="confirm-dialog__header">
            <AlertDialog.Title className="confirm-dialog__title">
              {title}
            </AlertDialog.Title>
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                className="confirm-dialog__close-button"
                aria-label="Cerrar"
              >
                <CircleX />
              </button>
            </AlertDialog.Cancel>
          </div>
          <AlertDialog.Description className="confirm-dialog__description">
            {description}
          </AlertDialog.Description>
          <div className="confirm-dialog__actions">
            {showCancel && (
              <AlertDialog.Cancel asChild>
                <Button
                  variant="secondary"
                  motionScale="base"
                  actionTrackSufix={cancelActionTrackSufix}
                >
                  {cancelLabel}
                </Button>
              </AlertDialog.Cancel>
            )}
            <AlertDialog.Action asChild>
              <Button
                variant={confirmVariant}
                motionScale="base"
                actionTrackSufix={confirmActionTrackSufix}
                onClick={handleConfirm}
              >
                {confirmLabel}
              </Button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
        </AlertDialog.Overlay>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};
